import { useState, useEffect } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

export interface ComputedFieldFormData {
  sheetName: string;
  cellRow: number;
  cellColumn: string;
  context: "test_results" | "control_documentation";
  instructions: string;
}

interface ComputedFieldFormProps {
  value: ComputedFieldFormData;
  onChange: (value: ComputedFieldFormData) => void;
  readOnly?: boolean;
}

export function ComputedFieldForm({
  value,
  onChange,
  readOnly = false,
}: ComputedFieldFormProps) {
  const [formData, setFormData] = useState<ComputedFieldFormData>(value);

  useEffect(() => {
    setFormData(value);
  }, [value]);

  const handleChange = (
    field: keyof ComputedFieldFormData,
    newValue: string | number,
  ) => {
    const updated = { ...formData, [field]: newValue };
    setFormData(updated);
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="sheetName">Sheet Name</Label>
          <Input
            id="sheetName"
            value={formData.sheetName}
            onChange={(e) => handleChange("sheetName", e.target.value)}
            placeholder="Sheet1"
            disabled={readOnly}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="cellRow">Row Number</Label>
          <Input
            id="cellRow"
            type="number"
            min={1}
            value={formData.cellRow}
            onChange={(e) =>
              handleChange("cellRow", parseInt(e.target.value, 10) || 1)
            }
            placeholder="1"
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="cellColumn">Column Letter</Label>
        <Input
          id="cellColumn"
          value={formData.cellColumn}
          onChange={(e) =>
            handleChange("cellColumn", e.target.value.toUpperCase())
          }
          placeholder="A"
          disabled={readOnly}
          className="uppercase"
        />
        <p className="text-sm text-muted-foreground">
          Use Excel column letters (A, B, C, ... Z, AA, AB, etc.)
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="context">Context Source</Label>
        <Select
          value={formData.context}
          onValueChange={(value) =>
            handleChange(
              "context",
              value as "test_results" | "control_documentation",
            )
          }
          disabled={readOnly}
        >
          <SelectTrigger id="context">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="test_results">Test Results</SelectItem>
            <SelectItem value="control_documentation">
              Control Documentation
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          The source of information the AI will use to generate this field
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="instructions">AI Generation Instructions</Label>
        <Textarea
          id="instructions"
          value={formData.instructions}
          onChange={(e) => handleChange("instructions", e.target.value)}
          placeholder="Example: Provide a 2-3 sentence summary of the test outcomes, highlighting any failures or exceptions"
          rows={5}
          disabled={readOnly}
        />
        <p className="text-sm text-muted-foreground">
          These instructions will guide the AI in generating content based on{" "}
          {formData.context === "test_results"
            ? "test results"
            : "control documentation"}
        </p>
      </div>

      <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm">
        <strong>Preview:</strong> {formData.sheetName}!{formData.cellColumn}
        {formData.cellRow} (AI-Generated)
      </div>
    </div>
  );
}
